import React, { useCallback, useMemo } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { MoreVertical } from '@lyearn/icons';
import { TypographyWithTooltip } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { AvatarGroupHorizontal, Stack, Typography } from '@lyearn/ui';
import { TypographyColors, TypographyVariants } from '@lyearn/ui';
import useNavigation from '@/hooks/useNavigation';
import { ContentsFragment } from '@/modules/corporate/modules/admin/graphql/fragments/generated/Contents';
import routes from '@/pages/corporate/routes';
import { useAdminRoute } from '@/pages/corporate/routes/hooks/useAdminRoutes';
import { KnowledgeAssetContentType, ResourceStatusEnum } from '@/types/schema';

import { logoStyle } from '../../styles';
import { getClassifiedChildKnowledgeAssets } from '../../utils';
import { renderKnowledgeAssetStatusPill } from '../StatusPill';

export interface UserDetailsOverrides {
  Email?: {
    Typography?: {
      variant?: TypographyVariants;
      color?: TypographyColors;
    };
  };
  Name?: {
    Typography?: {
      variant?: TypographyVariants;
      color?: TypographyColors;
    };
  };
  Icon?: {
    className?: string;
    size?: number;
  };
}

function SalesConversationCardGrid({
  salesConversation,
  index,
  hideActionMenu = true,
  isLearner,
  editOnClick,
  showStatusPill = false,
}: {
  salesConversation: ContentsFragment;
  index: number;
  hideActionMenu?: boolean;
  isLearner?: boolean;
  editOnClick?: boolean;
  showStatusPill?: boolean;
}) {
  const { id, name } = salesConversation;
  const navigation = useNavigation();
  const isAdminRoute = useAdminRoute();
  const { t } = useTranslation();
  const onClick = useCallback(() => {
    if (editOnClick) {
      navigation.replace(routes.KnowledgeAssetEditLearner(id, 'sales-conversation'), {
        appendQuery: false,
      });
      return;
    }
    if (isLearner || !isAdminRoute) {
      navigation.push(routes.KnowledgeAssetLearnerDetails(id, 'sales-conversation'));
      return;
    }
    const isDraft =
      (salesConversation as KnowledgeAssetContentType).status === ResourceStatusEnum.Draft;
    if (isDraft) {
      navigation.replace(routes.KnowledgeAssetEditor(id, 'sales-conversation'), {
        appendQuery: false,
      });
    } else {
      navigation.replace(routes.KnowledgeAssetDetails(id, 'sales-conversation'), {
        appendQuery: false,
      });
    }
  }, [id, navigation, isLearner, editOnClick, isAdminRoute, routes]);

  const { solutions, customerStories } = getClassifiedChildKnowledgeAssets(
    salesConversation as KnowledgeAssetContentType,
  );

  const solutionStr = solutions.length
    ? solutions?.[0] + (solutions?.length > 1 ? ` +${solutions?.length - 1}` : '')
    : '';

  const customerStoriesStr = customerStories.length ? customerStories?.[0] : 'No customer stories';
  const plusN =
    customerStories && customerStories?.length > 1 ? ` +${customerStories?.length - 1}` : null;

  const companyLogos = customerStories
    .map((customerStory) => ({
      name: customerStory,
      src: (salesConversation as KnowledgeAssetContentType)?.childKnowledgeAssets?.find(
        (child) => child?.name === customerStory,
      )?.cover?.url,
    }))
    .filter(Boolean)
    .slice(0, 4);

  const statusPill = useMemo(
    () =>
      showStatusPill
        ? renderKnowledgeAssetStatusPill(salesConversation as KnowledgeAssetContentType, t)
        : null,
    [showStatusPill, salesConversation, t],
  );

  return (
    <Stack
      className="w-[32.4rem] rounded-12 border cursor-pointer border-field px-20 py-20 hover:shadow-01 transition-shadow xs:min-h-full xs:w-full"
      direction="col"
      gapY={24}
      onClick={onClick}>
      <Stack gapX={12} justifyContent="between">
        <Stack direction="col">
          <Stack className="w-full overflow-hidden">
            <Typography numberOfLines={1} variant="heading-xs">
              {name}
            </Typography>
          </Stack>
          {solutionStr ? (
            <Stack alignItems="center" direction="row" gapX={4}>
              <Typography color="text-text-secondary" numberOfLines={2} variant="label-02">
                {solutionStr}
              </Typography>
            </Stack>
          ) : null}
        </Stack>

        {hideActionMenu ? null : (
          <Stack className="flex-shrink-0">
            <MoreVertical className="text-icon-secondary" height={20} width={20} />
          </Stack>
        )}
      </Stack>

      {statusPill ? (
        statusPill
      ) : (
        <Stack
          alignItems="center"
          className={cn(
            'bg-bg-tertiary rounded-24 pe-14 mt-auto w-fit',
            customerStories.length ? '' : 'py-[0.7rem] ps-14 pe-14 w-auto',
          )}
          direction="row"
          gapX={6}
          justifyContent="start">
          <AvatarGroupHorizontal
            avatars={companyLogos}
            className={logoStyle}
            classes={{ border: 'border-bg-tertiary' }}
            size="small"
          />
          <Stack>
            <TypographyWithTooltip
              as="span"
              color={customerStories.length ? 'text-text-primary' : 'text-text-secondary'}
              numberOfLines={1}
              variant="body-short-02">
              {customerStoriesStr}
            </TypographyWithTooltip>
          </Stack>
          {plusN ? (
            <Typography
              className="flex-shrink-0 me-6"
              color="text-text-primary"
              numberOfLines={1}
              variant="body-short-02">
              {plusN}
            </Typography>
          ) : null}
        </Stack>
      )}
    </Stack>
  );
}

export default SalesConversationCardGrid;
